import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environmentURL } from 'src/environments/environments';
import {
  ConsultaRegistroCita,
  CrearCitaPayload,
  CitaPendienteRegistro,
  EntidadCitas,
  ExcepcionCita,
  FechaDisponibilidad,
  HorarioDia,
  MaestroRegistroConsultaResponse,
  PanelJefaturaResponse,
  PerfilRepresentanteCitas,
  ReasignacionAusenciaAsignacion,
  ReasignacionAusenciaPreview,
  RegistroSinCitaPayload,
  RegistroTurnoResponse,
  SlotHora,
  TurnosAsignadosResponse,
  ExpedientesCitasConfigFecha,
  CitaFuturaExpediente,
  ExpedientesCitasSolicitarResponse,
  EstadisticasTurnosAbtResponse,
  LobbySnapshotResponse,
  LobbyMediaRuntime,
  LobbyMediaConfig,
  LobbyMediaArchivoDisponible,
  LobbyMediaCategoriaKey,
} from '../models/citas.models';

@Injectable({ providedIn: 'root' })
export class CitasService {
  private readonly baseUrl = environmentURL.apiBackendUrl;
  private readonly httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
  };

  constructor(private http: HttpClient) {}

  /** Nombre del usuario en sesión para auditoría */
  private usuarioAuditoria(): string {
    const nombre = (localStorage.getItem('nombre') ?? '').trim();
    return nombre.length > 200 ? nombre.slice(0, 200) : nombre;
  }

  listarRepresentantes(): Observable<any[]> {
    return this.http
      .get<{ items: any[] }>(`${this.baseUrl}citas/representantes`, this.httpOptions)
      .pipe(map((r) => r.items ?? []), catchError(this.manejarError));
  }

  crearRepresentante(usuarioInternoId: number, nombreMostrado?: string): Observable<any> {
    return this.http.post(`${this.baseUrl}citas/representantes`, {
      usuario_interno_id: usuarioInternoId,
      nombre_mostrado: nombreMostrado,
      sys_usuario: this.usuarioAuditoria(),
    }, this.httpOptions).pipe(catchError(this.manejarError));
  }

  cambiarEstadoRepresentante(id: number, activo: boolean): Observable<any> {
    return this.http.put(`${this.baseUrl}citas/representantes/${id}/estado`, {
      activo,
      sys_usuario: this.usuarioAuditoria(),
    }, this.httpOptions).pipe(catchError(this.manejarError));
  }

  /** Actualiza número de kárdex (pantalla lobby) y/o nombre mostrado */
  actualizarRepresentante(
    id: number,
    datos: { numero_kardex?: number | null; nombre_mostrado?: string | null },
  ): Observable<any> {
    return this.http.put(`${this.baseUrl}citas/representantes/${id}`, {
      ...datos,
      sys_usuario: this.usuarioAuditoria(),
    }, this.httpOptions).pipe(catchError(this.manejarError));
  }

  /** Snapshot público de atención + fila sin cita para pantalla lobby */
  obtenerLobbyAtencion(): Observable<LobbySnapshotResponse> {
    return this.http
      .get<LobbySnapshotResponse>(
        `${this.baseUrl}citas/lobby/atencion`,
        this.httpOptions,
      )
      .pipe(
        map((r) => ({
          items: r.items ?? [],
          cola_sin_cita: r.cola_sin_cita ?? [],
          segundos_rotacion: Number.isFinite(Number(r.segundos_rotacion))
            ? Number(r.segundos_rotacion)
            : 10,
          fecha: r.fecha,
        })),
        catchError(this.manejarError),
      );
  }

  /** Playlist runtime (URLs absolutas) para TV lobby */
  obtenerLobbyMediaRuntime(): Observable<LobbyMediaRuntime> {
    return this.http
      .get<LobbyMediaRuntime>(`${this.baseUrl}citas/lobby/media`, this.httpOptions)
      .pipe(
        map((r) => {
          // Comentario: 0 = videos deshabilitados; valores inválidos caen a 3
          const ciclosRaw = Number(r.institucionalCadaNCiclos);
          const ciclos = Number.isFinite(ciclosRaw)
            ? Math.max(0, Math.floor(ciclosRaw))
            : 3;
          return {
            institucionalCadaNCiclos: ciclos,
            mediaBaseUrl: r.mediaBaseUrl ?? null,
            institucional: ciclos === 0 ? [] : (r.institucional ?? []),
            esparcimiento: ciclos === 0 ? [] : (r.esparcimiento ?? []),
          };
        }),
        catchError(this.manejarError),
      );
  }

  /** Config editable LOBBY_MEDIA_CONFIG + LOBBY_SEGUNDOS_ROTACION (admin) */
  obtenerLobbyMediaConfig(): Observable<{
    config: LobbyMediaConfig;
    segundosRotacion: number;
    mediaBaseUrl: string | null;
    mediaRoot?: string | null;
  }> {
    return this.http
      .get<{
        config: LobbyMediaConfig;
        segundosRotacion?: number;
        mediaBaseUrl: string | null;
        mediaRoot?: string | null;
      }>(`${this.baseUrl}citas/lobby/media/config`, this.httpOptions)
      .pipe(
        map((r) => ({
          config: r.config,
          segundosRotacion: Number.isFinite(Number(r.segundosRotacion))
            ? Number(r.segundosRotacion)
            : 10,
          mediaBaseUrl: r.mediaBaseUrl,
          mediaRoot: r.mediaRoot,
        })),
        catchError(this.manejarError),
      );
  }

  /** Guarda playlist del turnómetro y segundos de rotación */
  guardarLobbyMediaConfig(
    config: LobbyMediaConfig,
    segundosRotacion: number,
  ): Observable<{ ok: boolean; config: LobbyMediaConfig; segundosRotacion: number }> {
    return this.http
      .put<{ ok: boolean; config: LobbyMediaConfig; segundosRotacion?: number }>(
        `${this.baseUrl}citas/lobby/media/config`,
        { config, segundosRotacion, sys_usuario: this.usuarioAuditoria() },
        this.httpOptions,
      )
      .pipe(
        map((r) => ({
          ok: r.ok,
          config: r.config,
          segundosRotacion: Number.isFinite(Number(r.segundosRotacion))
            ? Number(r.segundosRotacion)
            : segundosRotacion,
        })),
        catchError(this.manejarError),
      );
  }

  /** Lista videos de una carpeta vía escaneo del media server */
  listarLobbyMediaCarpeta(pathRelativo: string): Observable<LobbyMediaArchivoDisponible[]> {
    const params = new HttpParams().set('path', pathRelativo);
    return this.http
      .get<{ archivos: LobbyMediaArchivoDisponible[] }>(
        `${this.baseUrl}citas/lobby/media/listar`,
        { ...this.httpOptions, params },
      )
      .pipe(map((r) => r.archivos ?? []), catchError(this.manejarError));
  }

  /** Agrega carpeta a una categoría; el backend escanea e indexa videos */
  agregarLobbyMediaCarpeta(
    categoria: LobbyMediaCategoriaKey,
    pathRelativo: string,
    nombre?: string,
  ): Observable<{
    ok: boolean;
    config: LobbyMediaConfig;
    archivosDetectados: number;
    fuenteEscaneo: string;
  }> {
    return this.http
      .post<{
        ok: boolean;
        config: LobbyMediaConfig;
        archivosDetectados: number;
        fuenteEscaneo: string;
      }>(
        `${this.baseUrl}citas/lobby/media/carpetas`,
        {
          categoria,
          pathRelativo,
          nombre,
          sys_usuario: this.usuarioAuditoria(),
        },
        this.httpOptions,
      )
      .pipe(catchError(this.manejarError));
  }

  /** Quita carpeta y sus videos de la categoría */
  quitarLobbyMediaCarpeta(
    categoria: LobbyMediaCategoriaKey,
    carpetaId: string,
  ): Observable<{ ok: boolean; config: LobbyMediaConfig }> {
    const params = new HttpParams()
      .set('categoria', categoria)
      .set('carpetaId', carpetaId)
      .set('sys_usuario', this.usuarioAuditoria());
    return this.http
      .delete<{ ok: boolean; config: LobbyMediaConfig }>(
        `${this.baseUrl}citas/lobby/media/carpetas`,
        { ...this.httpOptions, params },
      )
      .pipe(catchError(this.manejarError));
  }

  /** Reescanear carpeta existente */
  reescanearLobbyMediaCarpeta(
    categoria: LobbyMediaCategoriaKey,
    carpetaId: string,
  ): Observable<{
    ok: boolean;
    config: LobbyMediaConfig;
    archivosDetectados: number;
    fuenteEscaneo: string;
  }> {
    return this.http
      .post<{
        ok: boolean;
        config: LobbyMediaConfig;
        archivosDetectados: number;
        fuenteEscaneo: string;
      }>(
        `${this.baseUrl}citas/lobby/media/carpetas/reescanear`,
        {
          categoria,
          carpetaId,
          sys_usuario: this.usuarioAuditoria(),
        },
        this.httpOptions,
      )
      .pipe(catchError(this.manejarError));
  }

  obtenerHorarios(entidad: EntidadCitas, entidadId: number): Observable<HorarioDia[]> {
    const params = new HttpParams()
      .set('entidad', entidad)
      .set('entidad_id', String(entidadId));
    return this.http
      .get<{ items: HorarioDia[] }>(`${this.baseUrl}citas/horarios`, { ...this.httpOptions, params })
      .pipe(map((r) => r.items ?? []), catchError(this.manejarError));
  }

  guardarHorarios(entidad: EntidadCitas, entidadId: number, horarios: HorarioDia[]): Observable<any> {
    return this.http.post(`${this.baseUrl}citas/horarios`, {
      horarios,
      sys_usuario: this.usuarioAuditoria(),
    }, {
      ...this.httpOptions,
      params: new HttpParams().set('entidad', entidad).set('entidad_id', String(entidadId)),
    }).pipe(catchError(this.manejarError));
  }

  obtenerExcepciones(entidad: EntidadCitas, entidadId: number): Observable<ExcepcionCita[]> {
    const params = new HttpParams()
      .set('entidad', entidad)
      .set('entidad_id', String(entidadId));
    return this.http
      .get<{ items: ExcepcionCita[] }>(`${this.baseUrl}citas/excepciones`, { ...this.httpOptions, params })
      .pipe(map((r) => r.items ?? []), catchError(this.manejarError));
  }

  guardarExcepcion(entidad: EntidadCitas, entidadId: number, exc: ExcepcionCita): Observable<any> {
    return this.http.post(`${this.baseUrl}citas/excepciones`, {
      ...exc,
      sys_usuario: this.usuarioAuditoria(),
    }, {
      ...this.httpOptions,
      params: new HttpParams().set('entidad', entidad).set('entidad_id', String(entidadId)),
    }).pipe(catchError(this.manejarError));
  }

  eliminarExcepcion(id: number): Observable<{ ok: boolean }> {
    return this.http
      .delete<{ ok: boolean }>(`${this.baseUrl}citas/excepciones/${id}`, this.httpOptions)
      .pipe(catchError(this.manejarError));
  }

  disponibilidadFechas(representanteId?: number): Observable<FechaDisponibilidad[]> {
    let params = new HttpParams();
    if (representanteId != null) {
      params = params.set('representante_id', String(representanteId));
    }
    return this.http
      .get<{ fechas: FechaDisponibilidad[] }>(`${this.baseUrl}citas/disponibilidad/fechas`, { ...this.httpOptions, params })
      .pipe(map((r) => r.fechas ?? []), catchError(this.manejarError));
  }

  disponibilidadHoras(fecha: string, representanteId?: number): Observable<SlotHora[]> {
    let params = new HttpParams().set('fecha', fecha);
    if (representanteId != null) {
      params = params.set('representante_id', String(representanteId));
    }
    return this.http
      .get<{ horarios: SlotHora[] }>(`${this.baseUrl}citas/disponibilidad/horas`, { ...this.httpOptions, params })
      .pipe(map((r) => r.horarios ?? []), catchError(this.manejarError));
  }

  crearCita(payload: CrearCitaPayload): Observable<any> {
    return this.http.post(`${this.baseUrl}citas`, {
      ...payload,
      origen: payload.origen ?? 'ADMINISTRACION',
      sys_usuario: this.usuarioAuditoria(),
    }, this.httpOptions).pipe(catchError(this.manejarError));
  }

  reagendarCita(id: number, datos: { fecha_cita: string; hora_inicio: string; hora_fin: string; motivo: string }): Observable<any> {
    return this.http.put(`${this.baseUrl}citas/${id}/reagendar`, {
      ...datos,
      sys_usuario: this.usuarioAuditoria(),
    }, this.httpOptions).pipe(catchError(this.manejarError));
  }

  cancelarCita(id: number, motivo: string): Observable<any> {
    return this.http.put(`${this.baseUrl}citas/${id}/cancelar`, {
      motivo,
      sys_usuario: this.usuarioAuditoria(),
    }, this.httpOptions).pipe(catchError(this.manejarError));
  }

  bitacoraCita(id: number): Observable<any[]> {
    return this.http
      .get<{ items: any[] }>(`${this.baseUrl}citas/${id}/bitacora`, this.httpOptions)
      .pipe(map((r) => r.items ?? []), catchError(this.manejarError));
  }

  consultarRegistroCita(folioMd5: string): Observable<ConsultaRegistroCita> {
    const params = new HttpParams().set('folio_md5', folioMd5);
    return this.http
      .get<ConsultaRegistroCita>(`${this.baseUrl}citas/registro/consulta`, { ...this.httpOptions, params })
      .pipe(catchError(this.manejarError));
  }

  consultarRegistroCitaPorId(citaId: number): Observable<ConsultaRegistroCita> {
    const params = new HttpParams().set('cita_id', String(citaId));
    return this.http
      .get<ConsultaRegistroCita>(`${this.baseUrl}citas/registro/consulta`, { ...this.httpOptions, params })
      .pipe(catchError(this.manejarError));
  }

  listarCitasPendientesRegistro(): Observable<CitaPendienteRegistro[]> {
    return this.http
      .get<{ items: CitaPendienteRegistro[] }>(`${this.baseUrl}citas/registro/pendientes`, this.httpOptions)
      .pipe(map((r) => r.items ?? []), catchError(this.manejarError));
  }

  consultarMaestroRegistro(maestroId: number): Observable<MaestroRegistroConsultaResponse> {
    const params = new HttpParams().set('maestro_id', String(maestroId));
    return this.http
      .get<MaestroRegistroConsultaResponse>(`${this.baseUrl}citas/registro/maestro`, { ...this.httpOptions, params })
      .pipe(catchError(this.manejarError));
  }

  registrarEntradaTurno(body: { folio_md5?: string; cita_id?: number }): Observable<RegistroTurnoResponse> {
    return this.http.post<RegistroTurnoResponse>(`${this.baseUrl}citas/turnos`, {
      ...body,
      tipo_registro: 'con_cita',
      sys_usuario: this.usuarioAuditoria(),
    }, this.httpOptions).pipe(catchError(this.manejarError));
  }

  registrarTurnoSinCita(payload: RegistroSinCitaPayload): Observable<RegistroTurnoResponse> {
    return this.http.post<RegistroTurnoResponse>(`${this.baseUrl}citas/turnos`, {
      ...payload,
      tipo_registro: 'sin_cita',
      sys_usuario: this.usuarioAuditoria(),
    }, this.httpOptions).pipe(catchError(this.manejarError));
  }

  obtenerPanelJefatura(fecha?: string): Observable<PanelJefaturaResponse> {
    let params = new HttpParams();
    if (fecha) {
      params = params.set('fecha', fecha);
    }
    return this.http
      .get<PanelJefaturaResponse>(`${this.baseUrl}citas/jefatura/panel`, { ...this.httpOptions, params })
      .pipe(catchError(this.manejarError));
  }

  /** ABT de turnos/citas del día: filas + conteos y promedios */
  obtenerEstadisticasTurnosAbt(fecha?: string): Observable<EstadisticasTurnosAbtResponse> {
    let params = new HttpParams();
    if (fecha) {
      params = params.set('fecha', fecha);
    }
    return this.http
      .get<EstadisticasTurnosAbtResponse>(
        `${this.baseUrl}citas/jefatura/estadisticas-turnos`,
        { ...this.httpOptions, params },
      )
      .pipe(catchError(this.manejarError));
  }

  asignarRepresentanteTurno(turnoId: number, representanteId: number): Observable<{ ok: boolean; id: number }> {
    return this.http.patch<{ ok: boolean; id: number }>(
      `${this.baseUrl}citas/turnos/${turnoId}/asignar-representante`,
      {
        representante_id: representanteId,
        sys_usuario: this.usuarioAuditoria(),
      },
      this.httpOptions,
    ).pipe(catchError(this.manejarError));
  }

  /**
   * Preview de reasignación por ausencia (excepción) o pendientes globales (chip).
   */
  previewReasignacionAusencia(params: {
    pendientes?: boolean;
    representante_id?: number;
    fecha?: string;
    tipo_excepcion?: string;
    hora_inicio?: string;
    hora_fin?: string;
  }): Observable<ReasignacionAusenciaPreview> {
    let httpParams = new HttpParams();
    if (params.pendientes) {
      httpParams = httpParams.set('pendientes', '1');
    }
    if (params.representante_id != null) {
      httpParams = httpParams.set('representante_id', String(params.representante_id));
    }
    if (params.fecha) {
      httpParams = httpParams.set('fecha', params.fecha);
    }
    if (params.tipo_excepcion) {
      httpParams = httpParams.set('tipo_excepcion', params.tipo_excepcion);
    }
    if (params.hora_inicio) {
      httpParams = httpParams.set('hora_inicio', params.hora_inicio);
    }
    if (params.hora_fin) {
      httpParams = httpParams.set('hora_fin', params.hora_fin);
    }
    return this.http
      .get<ReasignacionAusenciaPreview>(`${this.baseUrl}citas/jefatura/reasignacion-ausencia`, {
        ...this.httpOptions,
        params: httpParams,
      })
      .pipe(catchError(this.manejarError));
  }

  /** Aplica reasignaciones (destino o null = pendiente). */
  aplicarReasignacionAusencia(
    asignaciones: ReasignacionAusenciaAsignacion[],
    excepcionId?: number | null,
  ): Observable<{ ok: boolean; total_aplicados: number; omitidos: unknown[] }> {
    return this.http
      .post<{ ok: boolean; total_aplicados: number; omitidos: unknown[] }>(
        `${this.baseUrl}citas/jefatura/reasignacion-ausencia`,
        {
          asignaciones,
          excepcion_id: excepcionId ?? undefined,
          sys_usuario: this.usuarioAuditoria(),
        },
        this.httpOptions,
      )
      .pipe(catchError(this.manejarError));
  }

  calendarioRepresentante(id: number, desde?: string, hasta?: string): Observable<any[]> {
    let params = new HttpParams();
    if (desde) params = params.set('desde', desde);
    if (hasta) params = params.set('hasta', hasta);
    return this.http
      .get<{ items: any[] }>(`${this.baseUrl}citas/representantes/${id}/calendario`, { ...this.httpOptions, params })
      .pipe(map((r) => r.items ?? []), catchError(this.manejarError));
  }

  obtenerMiPerfilRepresentante(usuarioInternoId: number): Observable<PerfilRepresentanteCitas> {
    const params = new HttpParams().set('usuario_interno_id', String(usuarioInternoId));
    return this.http
      .get<PerfilRepresentanteCitas>(`${this.baseUrl}citas/representantes/mi-perfil`, { ...this.httpOptions, params })
      .pipe(catchError(this.manejarError));
  }

  listarTurnosAsignados(representanteId: number, fecha?: string): Observable<TurnosAsignadosResponse> {
    let params = new HttpParams();
    if (fecha) {
      params = params.set('fecha', fecha);
    }
    return this.http
      .get<TurnosAsignadosResponse>(
        `${this.baseUrl}citas/representantes/${representanteId}/turnos-asignados`,
        { ...this.httpOptions, params },
      )
      .pipe(catchError(this.manejarError));
  }

  iniciarAtencionTurno(turnoId: number, representanteId: number): Observable<{ ok: boolean; id: number }> {
    return this.http.patch<{ ok: boolean; id: number }>(
      `${this.baseUrl}citas/turnos/${turnoId}/iniciar-atencion`,
      {
        representante_id: representanteId,
        sys_usuario: this.usuarioAuditoria(),
      },
      this.httpOptions,
    ).pipe(catchError(this.manejarError));
  }

  pausarAtencionTurno(turnoId: number, representanteId: number): Observable<{ ok: boolean; id: number }> {
    return this.http.patch<{ ok: boolean; id: number }>(
      `${this.baseUrl}citas/turnos/${turnoId}/pausar-atencion`,
      {
        representante_id: representanteId,
        sys_usuario: this.usuarioAuditoria(),
      },
      this.httpOptions,
    ).pipe(catchError(this.manejarError));
  }

  concluirAtencionTurno(turnoId: number, representanteId: number): Observable<{ ok: boolean; id: number }> {
    return this.http.patch<{ ok: boolean; id: number }>(
      `${this.baseUrl}citas/turnos/${turnoId}/concluir-atencion`,
      {
        representante_id: representanteId,
        sys_usuario: this.usuarioAuditoria(),
      },
      this.httpOptions,
    ).pipe(catchError(this.manejarError));
  }

  vincularMaestroTurno(turnoId: number, representanteId: number, maestroId: number): Observable<{ ok: boolean; id: number }> {
    return this.http.patch<{ ok: boolean; id: number }>(
      `${this.baseUrl}citas/turnos/${turnoId}/vincular-maestro`,
      {
        representante_id: representanteId,
        maestro_id: maestroId,
        sys_usuario: this.usuarioAuditoria(),
      },
      this.httpOptions,
    ).pipe(catchError(this.manejarError));
  }

  registrarSolicitudInformacionTurno(
    turnoId: number,
    representanteId: number,
    payload: {
      nombre: string;
      apellido_paterno?: string;
      apellido_materno?: string;
      rfc?: string;
      telefono?: string;
      correo_electronico?: string;
    },
  ): Observable<{ ok: boolean; id: number; pidio_informes: boolean }> {
    return this.http.patch<{ ok: boolean; id: number; pidio_informes: boolean }>(
      `${this.baseUrl}citas/turnos/${turnoId}/solicitud-informacion`,
      {
        representante_id: representanteId,
        sys_usuario: this.usuarioAuditoria(),
        ...payload,
      },
      this.httpOptions,
    ).pipe(catchError(this.manejarError));
  }

  /** Fecha mínima y días laborables para el selector de expedientes por cita. */
  obtenerConfigExpedientesCitas(): Observable<ExpedientesCitasConfigFecha> {
    return this.http
      .get<ExpedientesCitasConfigFecha>(`${this.baseUrl}citas/expedientes/config-fecha`, this.httpOptions)
      .pipe(catchError(this.manejarError));
  }

  /** Citas futuras del representante en sesión para un día laborable. */
  listarCitasFuturasExpedientes(fecha: string, usuarioInternoId: number): Observable<{ items: CitaFuturaExpediente[]; fecha: string }> {
    const params = new HttpParams()
      .set('fecha', fecha)
      .set('usuario_interno_id', String(usuarioInternoId));
    return this.http
      .get<{ items: CitaFuturaExpediente[]; fecha: string }>(
        `${this.baseUrl}citas/expedientes/futuras`,
        { ...this.httpOptions, params },
      )
      .pipe(catchError(this.manejarError));
  }

  /** Solicita expedientes de archivo para las citas indicadas. */
  solicitarExpedientesCitas(citaIds: number[]): Observable<ExpedientesCitasSolicitarResponse> {
    return this.http.post<ExpedientesCitasSolicitarResponse>(
      `${this.baseUrl}citas/expedientes/solicitar`,
      {
        cita_ids: citaIds,
        usuario_id: localStorage.getItem('usuario_id') || '',
        sys_usuario_creacion: this.usuarioAuditoria(),
        usuario_interno_id: localStorage.getItem('usuario_id') || '',
      },
      this.httpOptions,
    ).pipe(catchError(this.manejarError));
  }

  private manejarError(error: unknown): Observable<never> {
    const err = error as { error?: { mensaje?: string }; message?: string };
    const msg = err?.error?.mensaje ?? err?.message ?? 'Error en servicio de citas';
    return throwError(() => new Error(msg));
  }
}
